import { ACTION } from "../../models/action";
import { APPLICATION } from "../../models/application";
import { SH_BILLABLE_LINE_ITEM } from "../../models/sh-billable-line-item";

/**
 * These are all the application states that will allow us to show the reminder to mark the project complete in the banner.
 *
 * @type {string[]}
 */
export const APPLICATION_STATES_TO_SHOW_MARK_PROJECT_COMPLETE_REMINDER = [
  APPLICATION.STATUS.COMPLETE_PRODUCER,
];

/**
 * This is the list of states where we will allow an expedite after the project has been completed.
 *
 * @type {string[]}
 */
export const PROJECT_DETAILS__APPLICATION_CHARGE_EXPEDITABLE = [
  APPLICATION.STATUS.COMPLETE_PUBLISHER,
  APPLICATION.STATUS.COMPLETED,
];

/**
 * The valid application statuses where a buyer can still edit the price
 *
 * @type {String[]}
 */
export const PROJECT_DETAILS__APPLICATION_EDITABLE_PRICE_STATES_BUYER = [
  APPLICATION.STATUS.ACTIVE,
];

/**
 * The valid application statuses where a buyer can still edit the application in most cases
 *
 * @type {String[]}
 */
export const PROJECT_DETAILS__APPLICATION_EDITABLE_STATES_BUYER = [
  APPLICATION.STATUS.PENDING,
  APPLICATION.STATUS.PUBLISHER_APPROVED,
  APPLICATION.STATUS.PRODUCER_APPROVED,
  APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE,
  APPLICATION.STATUS.ACTIVE,
  APPLICATION.STATUS.COMPLETE_PRODUCER,
];

/**
 * The valid application statuses where a seller can still edit the application in most cases
 *
 * @type {String[]}
 */
export const PROJECT_DETAILS__APPLICATION_EDITABLE_STATES_SELLER = [
  APPLICATION.STATUS.PENDING,
  APPLICATION.STATUS.PUBLISHER_APPROVED,
  APPLICATION.STATUS.PRODUCER_APPROVED,
  APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE,
  APPLICATION.STATUS.ACTIVE,
  APPLICATION.STATUS.COMPLETE_PRODUCER,
];

/**
 * The valid application statuses where a buyer can still edit the application in most cases
 *
 * @type {String[]}
 */
export const PROJECT_DETAILS__APPLICATION_EDITABLE_STORY_CODE_STATES_BUYER = [
  APPLICATION.STATUS.PENDING,
  APPLICATION.STATUS.PUBLISHER_APPROVED,
  APPLICATION.STATUS.PRODUCER_APPROVED,
  APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE,
  APPLICATION.STATUS.ACTIVE,
  APPLICATION.STATUS.COMPLETE_PRODUCER,
  APPLICATION.STATUS.COMPLETE_PUBLISHER,
  APPLICATION.STATUS.COMPLETED,
  APPLICATION.STATUS.PAID,
  APPLICATION.STATUS.DECLINED,
  APPLICATION.STATUS.CANCEL_PENDING,
  APPLICATION.STATUS.CANCELLED,
];

/**
 * The valid application statuses where a seller can still edit the application EXPENSES
 *
 * @type {String[]}
 */
export const PROJECT_DETAILS__APPLICATION_EDITABLE_EXPENSES_STATES_SELLER = [
  APPLICATION.STATUS.ACTIVE,
  APPLICATION.STATUS.COMPLETE_PRODUCER,
];

/**
 * The valid application statuses where a seller can edit the application EXPENSES while marking a project
 * complete. Projects with COMPLETE_PUBLISHER status will require re-approval by publisher.
 *
 * @type {String[]}
 */
export const PROJECT_DETAILS__APPLICATION_EDITABLE_EXPENSES_STATES_SELLER_COMPLETE_PROJECT = [
  APPLICATION.STATUS.ACTIVE,
  APPLICATION.STATUS.COMPLETE_PRODUCER,
  APPLICATION.STATUS.COMPLETE_PUBLISHER,
];

/**
 * The states where a seller can still edit a pitch.
 *
 * @type {string[]}
 */
export const PROJECT_DETAILS__APPLICATION_EDITABLE_PITCH_STATES_SELLER = [
  APPLICATION.STATUS.PENDING,
  APPLICATION.STATUS.PUBLISHER_APPROVED,
  APPLICATION.STATUS.PRODUCER_APPROVED,
  APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE,
  APPLICATION.STATUS.ACTIVE,
];

/**
 * Statuses that admins are allowed to re-open a project
 *
 * @type {string[]}
 */
export const PROJECT_DETAILS__APPLICATION_REOPENABLE_BY_ADMIN = [
  APPLICATION.STATUS.COMPLETED,
  APPLICATION.STATUS.CANCEL_PENDING,
  APPLICATION.STATUS.CANCELLED,
];

/**
 * The list of statuses where we can show the expiration timer/relative text on a project.
 *
 * @type {string[]}
 */
export const PROJECT_DETAILS__APPLICATION_EXPIRATION_STATUSES_ALLOWED = [
  ...APPLICATION.SIMPLE_STATUS_MAPPING[APPLICATION.SIMPLE_STATUS.PENDING],
];

/**
 * These action types will take priority when we have a tie for when the actions are created.  These would always be
 * given preference in the sort.
 *
 * @type {string[]}
 */
export const PROJECT_DETAILS__ACTIONS_WITH_PRIORITY = [
  ACTION.NAMES.PROJECT_ACTIVE,
  ACTION.NAMES.PROJECT_CANCELLED,
  ACTION.NAMES.PROJECT_COMPLETED,
];

/**
 * These are the timeouts to auto-decline an application/pitch in days.
 *
 * @type {Object}
 */
export const PROJECT_DETAILS__APPLICATION_TIMEOUTS = {
  HIRE: 7,
  PITCH: 14,
  DIRECT: 28,
};

/**
 * All banner types for project details.
 *
 * @type {Object}
 */
export const PROJECT_DETAILS__BANNER_TYPES = {
  DECLINE_REMINDER: 'decline-reminder',
  ABOUT_TO_EXPIRE: 'about-to-expire',
  MARK_COMPLETE_REMINDER: 'mark-complete-reminder'
};

/**
 * @type {string[]}   billable item types support by the 'other' category in the UI.
 */
export const PROJECT_DETAILS__OTHER_ITEM_TYPES = [
  SH_BILLABLE_LINE_ITEM.TYPES.ADJUSTMENT
];

/**
 * This is the timeout between polls to the server to refresh the application that we are on.
 *
 * @type {number}
 */
export const PROJECT_DETAILS__REFRESH_TIMEOUT_MS = 120000;

/**
 * Available titles for the sub nav for the project details screen.
 *
 * @type {Object}
 */
export const PROJECT_DETAILS__SUB_NAV_TITLES = {
  ACTIVITY: 'Activity',
  DETAILS: 'Details',
  DELIVERABLES: 'Deliverables',
  FILES: 'Files',
  PAYMENT: 'Payment',
  PITCH: 'Pitch',
};

/**
 * Available titles for the sub nav for the submitted application overview.
 *
 * @type {Object}
 */
export const APP_DETAILS__SUB_NAV_TITLES = {
  ACTIVITY: 'Application Activity',
  DETAILS: 'Original Posting',
  PITCH: 'Pitch',
};

/**
 * Shared include string used throughout the project-details experience.
 *
 * @type {Object}
 */
export const PROJECT_DETAILS__PROGRESSIVE_INCLUDES = {
  FILES: [
    'assets.uploader.company',
    'assets.uploader.location',
    'assets.uploader',
    'assets.uri-collection'
  ],
  PITCH: [
    'pitch.location',
    'pitch.weburls',
    'pitch.image'
  ],
  THREAD: [
    'actions',
    'actions.user',
    'actions.user.location',
    'actions.user.company',
    'actions.user.company.legals',
    'thread.messages',
    'thread.messages.actions',
    'thread.messages.user',
    'thread.messages.assets.uri-collection',
  ],
};

export function getProjectDetailsPaymentIncludes(user) {
  const includes = [
    'sh-payments',
    'sh-payments.expedite-requestor',
    'sh-payments.expedite-requestor.company',
    'sh-payment-line-items',
    'project-line-items',
    'fee-structure',
  ];

  if (user.isBuyer || user.isAdmin) {
    const buyerAndAdmin = [
      'sh-invoice-line-items.sh-invoice.fee-structure',
      'sh-invoice-line-items.sh-invoice.sh-payment',
      'sh-invoice-line-items.related-sh-invoice-line-item',
      'sh-invoice-line-items.derived-from-sh-invoice-line-item',
      'sh-invoice-line-items.derived-from-project-line-item',
      'sh-invoice-line-items',
      'sh-invoices',
    ];
    includes.push(...buyerAndAdmin);
  }

  if (user.isSeller || user.isAdmin) {
    const sellerAndAdmin = [
      'sh-payment-line-items.derived-from-project-line-item',
      'sh-payment-line-items.derived-from-sh-payment-line-item',
      'sh-payment-line-items.related-sh-payment-line-item'
    ];
    includes.push(...sellerAndAdmin);
  }

  if (user.isAdmin) {
    const adminOnly = [
      'sh-invoice-line-items.sh-invoice.sh-payment.fee-structure',
      'sh-payment-line-items.sh-payment.sh-invoice',
      'sh-payment-line-items.sh-invoice.sh-payment.fee-structure',
      'sh-payment-line-items.sh-invoice.fee-structure',
    ];
    includes.push(...adminOnly);
  }

  if (user.isSeller) {
    const sellerOnly = [
      'sh-payment-line-items.sh-payment',
      'sh-payment-line-items.sh-payment.fee-structure',
    ];
    includes.push(...sellerOnly);
  }


  return includes;
}

/**
 * These are the shared includes for the application details experience
 *
 * NOTE -- use the actual function to get these since it will depend on the user which includes
 * we can pull in.
 *
 * @type {string[]}
 */
export const PROJECT_DETAILS__BASE_APPLICATION_INCLUDES = [
  'application-deliverables',
  'application-details.location',
  'application-schedules',
  'assignment.assignment-deliverables',
  'assignment.assignment-schedules',
  'assignment.location',
  'brand.image',
  'buyer',
  'company',
  'company.legals',
  'followers',
  'legal-agreements.legal',
  'location',
  'pitch',
  'projectdigest',
  'projectdigest.classifications',
  'projectdigest.company',
  'projectdigest.oembed',
  'projectdigest.projectdigest-verifications:verified_or_latest',
  'projectdigest.review.reviewer',
  'projectdigest.services',
  'projectdigest.video-category',
  'projectdigest.video-topics',
  'seller.location',
  'seller',
  'services',
  'thread',
];

export function getProjectDetailsIncludes(user) {
  // would mutate on subsequent calls without this!
  const includes = Object.assign([], PROJECT_DETAILS__BASE_APPLICATION_INCLUDES);

  if (user.isBuyer || user.isAdmin) {
    includes.push('company.company-teams');
    includes.push('company.company-memberships.user');
    includes.push('company.company-memberships.company-team-memberships.company-team');
    includes.push('cost-code');
    includes.push('assignment.company-team');
  }

  return includes;
}
